import { createContext, type PropsWithChildren, useContext } from "react"
import { getAllServices } from "@modules/core/services/services"
import { getDevFakeServices } from "@modules/core/services/services.fake"

type ContextServices = ReturnType<typeof getAllServices>

export const ServicesContext = createContext<ContextServices>({} as ContextServices)

/**
 * Enable fake services by setting VITE_USE_FAKE_SERVICES=true in your .env.local file
 */
const USE_FAKE_SERVICES = import.meta.env.VITE_USE_FAKE_SERVICES === "true"

/**
 * This context provider is used to provide all services to the application.
 * It is used in the root of the application.
 * The getAllServices accepts an object of services to override the default ones.
 * This is useful for testing purposes and injecting fake services.
 * @example getAllServices({ customBenchmarkApi: new FakeBenchmarkApi() })
 *
 * To use fake services for local development, set VITE_USE_FAKE_SERVICES=true in .env.local
 */

export const ServicesContextProvider = ({ children }: PropsWithChildren) => {
  const services = USE_FAKE_SERVICES ? getDevFakeServices() : getAllServices({})

  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>
}

export const useServicesContext = () => useContext(ServicesContext)
